import { useEffect, useState } from 'react';
import clsx from 'clsx';

import { Input } from '../Input';
import Button from '../Button';
import { SearchIcon } from '../SearchBar/SearchIcon';
import type { ButtonProps } from '../Button';

export type ApplySearchInputProps = {
  value?: string;
  onChange?: (newValue: string) => void;
  placeholder?: string;
  applyLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  inputClassName?: string;
  trimOnApply?: boolean;
  showSearchIcon?: boolean;
  buttonType?: ButtonProps['type'];
  buttonSize?: ButtonProps['size'];
  minLength?: number;
};

export const ApplySearchInput: React.FC<ApplySearchInputProps> = ({
  value = '',
  onChange,
  placeholder = 'Search',
  applyLabel = 'Search',
  disabled = false,
  loading = false,
  className,
  inputClassName = 'pl-14 text-medium',
  trimOnApply = true,
  showSearchIcon = true,
  buttonType = 'outline',
  buttonSize = 'small',
  minLength = 3,
}) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const getAppliedValue = (nextValue: string) =>
    trimOnApply ? nextValue.trim() : nextValue;

  const appliedValue = getAppliedValue(value ?? '');
  const draftValue = getAppliedValue(draft);
  const hasMinLength = draftValue.length >= minLength;
  const isClearing = draftValue.length === 0 && appliedValue.length > 0;
  const hasChanges = draftValue !== appliedValue;
  const canApply = hasChanges && (hasMinLength || isClearing);
  const isApplyDisabled = disabled || loading || !canApply;

  const handleApply = () => {
    if (!canApply) return;

    onChange?.(draftValue);
  };

  return (
    <div className={clsx('flex gap-x-sm items-stretch w-full', className)}>
      <Input
        value={draft}
        onChange={setDraft}
        onSubmit={handleApply}
        placeholder={placeholder}
        inputClassName={inputClassName}
        prefix={showSearchIcon ? <SearchIcon /> : undefined}
        disabled={disabled}
        className="flex-1 min-w-0"
        defaultSizeClass="w-full"
        isChild
      />
      <Button
        type={buttonType}
        size={buttonSize}
        label={applyLabel}
        loading={loading}
        onClick={handleApply}
        disabled={isApplyDisabled}
        className="shrink-0 rounded-bt"
      />
    </div>
  );
};
